import React, { useId } from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { Option, RadioProps } from '../../types';

export const Radio: React.FC<RadioProps> = (props) => {
  const {
    name,
    label,
    value,
    options,
    onChange,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    orientation = 'vertical',
    renderLabel,
    className,
    radioClassName,
    labelClassName
  } = props;

  const groupId = useId();

  const handleChange = (optionValue: string) => {
    if (!isDisabled) {
      onChange?.(optionValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, option: Option) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!option.disabled && !isDisabled) {
        handleChange(option.value);
      }
    }
  };

  const hasError = Boolean(error);
  const showHelperText = !hasError && helperText;

  return (
    <div
      className={cn(DEFAULT_STYLES.field, className, 'base__radio')}
      role="radiogroup"
      aria-labelledby={`${groupId}-label`}
    >
      {label && (
        <div
          id={`${groupId}-label`}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            'mb-2',
            labelClassName
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </div>
      )}

      <div
        className={cn(
          orientation === 'horizontal'
            ? DEFAULT_STYLES.radioHorizontal
            : DEFAULT_STYLES.radioVertical
        )}
      >
        {options.map((option) => {
          const optionId = `${groupId}-${option.value}`;
          const isSelected = value === option.value;
          const isOptionDisabled = isDisabled || option.disabled;

          return (
            <label
              key={option.value}
              htmlFor={optionId}
              className={cn(
                'flex items-center gap-2 cursor-pointer group',
                isOptionDisabled && 'opacity-50 cursor-not-allowed',
                radioClassName
              )}
              onKeyDown={(e) => handleKeyDown(e, option)}
              tabIndex={isOptionDisabled ? -1 : 0}
            >
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => handleChange(option.value)}
                disabled={isOptionDisabled}
                className="sr-only"
                aria-describedby={hasError ? `${groupId}-error` : undefined}
              />

              {/* Custom Radio Circle - 16px total (border-box), 6px inner dot, 1px border */}
              <span
                className={cn(
                  'flex items-center justify-center rounded-full flex-shrink-0',
                  'w-4 h-4 p-1',
                  'box-border transition-colors duration-200',
                  // Default (unselected) - 1px border, white background
                  !isSelected &&
                    !isOptionDisabled && [
                      'bg-white border border-lightBlueGray300',
                      // Hover: darker border (medium-dark gray) - works when hovering anywhere on label
                      'group-hover:border-gullGray',
                      // Focus: visible focus ring for keyboard navigation
                      'group-focus-within:border-lightBlueGray300',
                      'group-focus-within:shadow-focus-ring-gray',
                      'group-focus-visible:border-lightBlueGray300',
                      'group-focus-visible:shadow-focus-ring-gray'
                    ],
                  // Default (selected) - no border when selected
                  isSelected &&
                    !isOptionDisabled && [
                      'bg-lightIndigo600 border-0',
                      // Hover: slightly darker indigo - works when hovering anywhere on label
                      'group-hover:bg-lightIndigo700',
                      // Focus: visible focus ring (lighter purple/blue ring) for keyboard navigation
                      'group-focus-within:bg-lightIndigo600',
                      'group-focus-within:shadow-focus-ring-indigo-light',
                      'group-focus-visible:bg-lightIndigo600',
                      'group-focus-visible:shadow-focus-ring-indigo-light'
                    ],
                  // Disabled (unselected) - 1px border
                  !isSelected &&
                    isOptionDisabled && [
                      'bg-catskillWhite1 border border-lightBlueGray300'
                    ],
                  // Disabled (selected) - no border when selected
                  isSelected &&
                    isOptionDisabled && ['bg-lightIndigo300 border-0']
                )}
              >
                {isSelected && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
                )}
              </span>

              {/* Label */}
              <span
                className={cn(
                  'text-sm font-medium leading-5 tracking-tight',
                  // Default/Hover/Focused (unselected)
                  !isSelected && !isOptionDisabled && 'text-darkLightBlueGray',
                  // Selected states
                  isSelected && !isOptionDisabled && 'text-lightblueGray800',
                  // Disabled states
                  isOptionDisabled && 'text-lightBlueGray300'
                )}
              >
                {renderLabel ? renderLabel(option) : option.label}
              </span>
            </label>
          );
        })}
      </div>

      {/* Single feedback container - always rendered to prevent layout shift */}
      <p
        id={`${groupId}-error`}
        className={cn(
          DEFAULT_STYLES.field__message,
          hasError
            ? 'text-errorTextColor'
            : showHelperText
              ? 'text-helperTextColor'
              : 'invisible h-4'
        )}
        role={hasError ? 'alert' : undefined}
      >
        {hasError ? error : showHelperText ? helperText : ''}
      </p>
    </div>
  );
};

Radio.displayName = 'Radio';
